import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { parsePoundsInput, poundsInputText } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Field, HoverBubble, Input, Select, askText } from '@/components/ui';

/** t118: what a milestone is for, shown next to every Milestone field. */
export const MILESTONE_HELP = 'A milestone groups tasks toward a project checkpoint or date, such as "Christmas stock ready". The project shows how many of its tasks are done.';

export function MilestoneLabel() {
  return (
    <span className="inline-flex items-center gap-1">Milestone
      <HoverBubble bubble={MILESTONE_HELP}><Info className="h-3.5 w-3.5 text-slate-400" aria-label="What is a milestone?" tabIndex={0} data-testid="milestone-help" /></HoverBubble>
    </span>
  );
}

/** Collapsed "More" section (t118): less-used task fields stay out of the way until asked for. */
export function MoreSection({ children, defaultOpen = false, count = 0 }: { children: ReactNode; defaultOpen?: boolean; count?: number }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid="task-more">
      <button type="button" aria-expanded={open} onClick={() => setOpen(o => !o)} className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white" data-testid="task-more-toggle">
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}More{!open && count > 0 ? ` (${count} set)` : ''}
      </button>
      {open && <div className="mt-2 space-y-3">{children}</div>}
    </div>
  );
}

const NEW = '__new__';
export const areasKey = (ws: string, loc: string) => ['task-areas', ws, loc] as const;

/** Area inside a building (t118). Lists that building's areas; "New area…" adds one. */
export function AreaSelect({ locationId, value, onChange, disabled, className, id }: { locationId: string | null | undefined; value: string | null | undefined; onChange: (id: string | null) => void; disabled?: boolean; className?: string; id?: string }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: areasKey(ws ?? '', locationId ?? ''), queryFn: () => api.taskAreas(ws!, locationId!), enabled: Boolean(ws && locationId), staleTime: 60_000 });
  const pick = async (v: string) => {
    if (v !== NEW) { onChange(v || null); return; }
    const name = await askText({ title: 'New area', confirmLabel: 'Add area', danger: false, input: { label: 'Area name', placeholder: 'e.g. Yard, Goods in, Shop floor', required: true, maxLength: 80 } });
    if (!name?.trim() || !ws || !locationId) return;
    try {
      const a = await api.createTaskArea(ws, { location_id: locationId, name: name.trim() });
      await queryClient.invalidateQueries({ queryKey: areasKey(ws, locationId) });
      onChange(a.id);
    } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return (
    <Select id={id} aria-label="Area" className={className} disabled={disabled || !locationId} value={value ?? ''} onChange={e => void pick(e.target.value)} data-testid="task-area">
      <option value="">{locationId ? 'None' : 'Pick a building first'}</option>
      {(q.data?.areas ?? []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      {locationId && !disabled && <option value={NEW}>+ New area…</option>}
    </Select>
  );
}

/** Pounds in, pence out (budget). Empty = no budget. Shows "1,500.00" (t171u: the shared en-GB money format). */
export function BudgetInput({ value, onChange, disabled, className, id }: { value: number | null | undefined; onChange: (pence: number | null) => void; disabled?: boolean; className?: string; id?: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className="text-sm text-slate-500">£</span>
      <Input id={id} aria-label="Budget" type="text" inputMode="decimal" className={className} disabled={disabled} defaultValue={poundsInputText(value)} key={value ?? 'none'}
        onBlur={e => {
          const p = parsePoundsInput(e.target.value);
          if (p === undefined || (p !== null && p < 0)) { e.target.value = poundsInputText(value); return; } // not money: put the old value back
          if (p !== (value ?? null)) onChange(p); else e.target.value = poundsInputText(value);
        }} data-testid="task-budget" />
    </span>
  );
}

/** Hours in, minutes out (estimate). */
export function EstimateInput({ value, onChange, disabled, className, id }: { value: number | null | undefined; onChange: (min: number | null) => void; disabled?: boolean; className?: string; id?: string }) {
  return (
    <span className="flex items-center gap-2">
      <Input id={id} aria-label="Estimate in hours" type="number" min={0} step="0.25" className={className} disabled={disabled} defaultValue={value ? value / 60 : ''} key={value ?? 'none'}
        onBlur={e => { const m = e.target.value === '' ? null : Math.round(Number(e.target.value) * 60); if (m !== null && (!Number.isFinite(m) || m < 0)) return; if (m !== (value ?? null)) onChange(m); }} data-testid="task-estimate" />
      <span className="text-xs text-slate-500">hours</span>
    </span>
  );
}

/**
 * t168u: Milestone for a task in a project (New task and Create task from message). Shows only when the project has
 * open milestones, and clears a milestone that isn't in the chosen project (switching project kept the old one).
 */
export function MilestoneField({ projectId, value, onChange }: { projectId: string; value: string; onChange: (id: string) => void }) {
  const q = useQuery({ queryKey: ['project', projectId], queryFn: () => api.project(projectId), enabled: Boolean(projectId) });
  const milestones = projectId ? (q.data?.milestones ?? []).filter(m => m.status === 'open') : [];
  const stale = Boolean(value) && (!projectId || (q.data && !milestones.some(m => m.id === value)));
  useEffect(() => { if (stale) onChange(''); }, [stale, onChange]);
  if (!projectId || milestones.length === 0) return null;
  return (
    <Field label="Milestone" hint={<HoverBubble bubble={MILESTONE_HELP}><Info className="h-3.5 w-3.5 text-slate-400" aria-label="What is a milestone?" tabIndex={0} data-testid="milestone-help" /></HoverBubble>}>
      <Select value={value} onChange={e => onChange(e.target.value)} data-testid="new-task-milestone"><option value="">None</option>{milestones.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</Select>
    </Field>
  );
}
